import { Signal } from "@char/aftercare";
import { getProfile, ProfileView } from "../queries.ts";
import { RpcClient } from "./api.ts";
import { authState, viewTransport } from "./auth.ts";
import { cacheProfile } from "./view-cache.ts";

/**
 * The signed-in account's profile as the AppView last described it. Read-after-write views borrow
 * its identity, and the composer its channels, without fetching them again.
 */
export const ownProfile = new Signal<ProfileView | undefined>(undefined);

const storageKey = (did: string) => `cerulea.profile.${did}`;
let flight: AbortController | undefined;

/** Follow the signed-in account: its stored copy at once, then fresh ones as the tab returns. */
export function trackOwnProfile(): void {
  authState.subscribeImmediate((state) => {
    flight?.abort();
    const seed = state.status === "authenticated" ? stored(state.did) : undefined;
    ownProfile.set(seed);
    if (seed) cacheProfile(seed);
    refreshOwnProfile();
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) refreshOwnProfile();
  });
}

function refreshOwnProfile(): void {
  const state = authState.get();
  if (state.status !== "authenticated") return;
  flight?.abort();
  const controller = flight = new AbortController();
  void new RpcClient(viewTransport(getProfile.id)).call(getProfile, {
    parameters: { actor: state.did },
    signal: controller.signal,
  }).then((view) => {
    if (!controller.signal.aborted) rememberProfile(view);
  }).catch(() => {
    // Offline, or no profile yet: the last known copy still stands.
  });
}

/** A profile view arrived: keep it, and keep it locally if it describes the signed-in account. */
export function rememberProfile(view: ProfileView): void {
  cacheProfile(view);
  const state = authState.get();
  if (state.status === "authenticated" && state.did === view.actor.did) store(view);
}

/** Reflect the account's own write locally until the AppView catches up. */
export function patchOwnProfile(did: string, update: (view: ProfileView) => ProfileView): void {
  const view = ownProfile.get();
  if (view?.actor.did === did) store(update(view));
}

function store(view: ProfileView): void {
  ownProfile.set(view);
  cacheProfile(view);
  try {
    localStorage.setItem(storageKey(view.actor.did), JSON.stringify(view));
  } catch {
    // Without storage the copy lasts as long as the tab.
  }
}

function stored(did: string): ProfileView | undefined {
  try {
    const parsed = ProfileView.validate(
      JSON.parse(localStorage.getItem(storageKey(did)) ?? "null"),
    );
    return parsed.success && parsed.value.actor.did === did ? parsed.value : undefined;
  } catch {
    return undefined;
  }
}
